import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import { ArrowLeft, Sparkles, Mic } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AffirmationLibraryPage = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [affirmations, setAffirmations] = useState([]);
  const [hasVoice, setHasVoice] = useState(false);

  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    const authToken = getToken();
    
    try {
      // Fetch category info, affirmations, and voice status
      const [catsRes, affsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/voice-studio/categories`),
        fetch(`${API_URL}/api/voice-studio/categories/${categoryId}/affirmations`),
        authToken 
          ? fetch(`${API_URL}/api/voice-studio/status`, { headers: { Authorization: `Bearer ${authToken}` }})
          : Promise.resolve({ json: () => ({ has_voice: false }) })
      ]);

      const [catsData, affsData, statusData] = await Promise.all([
        catsRes.json(),
        affsRes.json(),
        statusRes.json()
      ]);

      const cat = catsData.categories?.find(c => c.id === categoryId);
      setCategory(cat);
      setAffirmations(affsData.affirmations || []);
      setHasVoice(statusData.has_voice || false);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAffirmation = (affirmation) => {
    if (hasVoice) {
      navigate(`/voice-studio/create?text=${encodeURIComponent(affirmation.text)}&category=${categoryId}`);
    } else {
      navigate("/voice-studio/record");
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-[#FAFAF7] pb-28">
        {/* Header */}
        <div className="bg-gradient-to-b from-[#2D4A2A] to-[#3D5A34] text-white px-6 pt-6 pb-12">
          <div className="max-w-lg mx-auto">
            <button
              onClick={() => navigate("/voice-studio?tab=library")}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Library</span>
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
                <Sparkles size={24} className="text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl mb-1">{category?.name || "Affirmations"}</h1>
                <p className="text-white/70 text-sm">{category?.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 -mt-6 max-w-lg mx-auto space-y-4">
          {!hasVoice && (
            <Card className="p-4 bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-3">
                <Mic size={20} className="text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800">Record your voice first to create installations</p>
                </div>
                <Button size="sm" onClick={() => navigate("/voice-studio/record")}>
                  Record
                </Button>
              </div>
            </Card>
          )}

          {affirmations.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-[#6B7280]">No affirmations in this category yet</p>
            </Card>
          ) : (
            affirmations.map((affirmation, index) => (
              <motion.div
                key={affirmation.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className="p-5 cursor-pointer hover:-translate-y-0.5 transition-all group rounded-2xl bg-white"
                  style={{
                    border: '1px solid rgba(212,163,115,0.32)',
                    boxShadow: '0 0 24px rgba(212,163,115,0.06), 0 1px 0 rgba(212,163,115,0.10) inset',
                  }}
                  onClick={() => handleSelectAffirmation(affirmation)}
                  data-testid={`affirmation-card-${affirmation.id}`}
                >
                  <p
                    className="text-[#1F2937] italic leading-relaxed"
                    style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: '1.18rem' }}
                  >
                    "{affirmation.text}"
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    {affirmation.featured && (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(212,163,115,0.18)', color: '#8B5E34' }}>
                        Featured
                      </span>
                    )}
                    <span className="text-xs text-[#4A6741] opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                      {hasVoice ? "Create Installation →" : "Record Voice First →"}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default AffirmationLibraryPage;
